import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";

import { fetchProfile, useUser } from "@/entities/Auth";
import { AccountActionText } from "@/shared/config/strings";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { useAppDispatch, useAppSelector } from "@/shared/lib/hooks/redux";
import { useConfirmModal } from "@/shared/lib/hooks/useConfirmModal";
import { useTheme } from "@/shared/lib/hooks/useTheme";
import { ActionBtn } from "@/shared/ui/Btn";
import Button from "@/shared/ui/Button/Button";
import { Dialog } from "@/shared/ui/Dialog";
import LoadingSpinner from "@/shared/ui/LoadingSpinner";
import { Title, TitleSize } from "@/shared/ui/Title";
import {
  cancelSubscription,
  createSubscription,
  skipSubscription,
  uncancelSubscription,
  unskipSubscription,
} from "@/shared/utils/api";
import { getFancyTime } from "@/shared/utils/utils";

import { invalidateSubscriptionChangePreview, useGetSubscriptionChangePreviewQuery } from "../../api/subscriptionApi";
import { SubscriptionChangeText } from "../../const/strings";
import { ProductInterval, SubscriptionStatus } from "../../const/subscription";
import { getSubscriptionChangeDirection } from "../../lib/getSubscriptionChangeDirection";
import { useSubscription } from "../../lib/useSubscription";
import { getDetails, getProductsList } from "../../model/selectors/subscriptionSelectors";
import { downgradeSubscription } from "../../model/services/downgradeSubscription";
import { fetchDetails } from "../../model/services/fetchDetails";
import { upgradeSubscription } from "../../model/services/upgradeSubscription";
import { PaymentFailed } from "../PaymentFailed/PaymentFailed";
import { ProductSelection } from "../ProductSelection/ProductSelection";

import cls from "./ManagePlan.module.scss";

interface ManagePlanProps {
  className?: string;
}

export const ManagePlan = memo(({ className }: ManagePlanProps) => {
  const dispatch = useAppDispatch();
  const subscription = useSubscription();
  const currentUser = useUser();
  const { showAlert } = useAlert();
  const { theme } = useTheme();

  const productsList = useAppSelector(getProductsList);
  const subscriptionDetails = useAppSelector(getDetails);

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [reactivateStripePriceId, setReactivateStripePriceId] = useState(currentUser.product?.stripePriceId);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  const [isChangePlanBusy, setIsChangePlanBusy] = useState(false);

  const { modalNode, withModalConfirm } = useConfirmModal();

  useEffect(() => {
    if (!selectedProduct) {
      setSelectedProduct(currentUser.product?.stripePriceId);
    }
  }, [selectedProduct, currentUser.product]);

  const isDifferentPlanSelected = Boolean(selectedProduct) && selectedProduct !== currentUser.product?.stripePriceId;

  const { data: changePreview, isFetching: isPreviewFetching } = useGetSubscriptionChangePreviewQuery(
    { toPriceId: selectedProduct ?? "" },
    { skip: !isDifferentPlanSelected }
  );

  const proratedAmountNode = useMemo(() => {
    if (!isDifferentPlanSelected) {
      return undefined;
    }
    if (isPreviewFetching) {
      return <LoadingSpinner isSmall={true} className={cls.InlineSpinner} />;
    }
    if (changePreview?.proratedAmount === undefined) {
      return undefined;
    }
    return `$${changePreview.proratedAmount}`;
  }, [isDifferentPlanSelected, isPreviewFetching, changePreview?.proratedAmount]);

  // get current and selected products for comparison
  const currentProduct = useMemo(() => {
    return productsList.find((p) => p.stripePriceId === currentUser.product?.stripePriceId);
  }, [productsList, currentUser.product?.stripePriceId]);

  const selectedProductData = useMemo(() => {
    if (!selectedProduct) {
      return null;
    }
    return productsList.find((p) => p.stripePriceId === selectedProduct);
  }, [productsList, selectedProduct]);

  const { isUpgrade, isDowngrade } = useMemo(
    () => getSubscriptionChangeDirection(currentProduct, selectedProductData),
    [currentProduct, selectedProductData]
  );

  // generate modal text based on a plan change type
  const getChangePlanModalText = useMemo(() => {
    if (!currentProduct || !selectedProductData || !subscriptionDetails) {
      return AccountActionText.upgradeText;
    }

    const currentInterval = currentProduct.interval;
    const selectedInterval = selectedProductData.interval;
    const isMonthlyToYearly =
      currentInterval === ProductInterval.Monthly && selectedInterval === ProductInterval.Yearly;
    const isYearlyToMonthly =
      currentInterval === ProductInterval.Yearly && selectedInterval === ProductInterval.Monthly;
    const isSameInterval = currentInterval === selectedInterval;

    const nextBillingDate = subscriptionDetails.currentPeriodEnd
      ? getFancyTime(subscriptionDetails.currentPeriodEnd * 1000)
      : "your next billing cycle";

    // determine the change type and return the appropriate message
    if (isMonthlyToYearly && isUpgrade) {
      return SubscriptionChangeText.monthlyToYearlyUpgradeText(proratedAmountNode);
    } else if (isYearlyToMonthly && isDowngrade) {
      return SubscriptionChangeText.yearlyToMonthlyDowngradeText(nextBillingDate);
    } else if (isSameInterval && currentInterval === ProductInterval.Monthly) {
      if (isUpgrade) {
        return SubscriptionChangeText.monthlyUpgradeText(proratedAmountNode);
      } else if (isDowngrade) {
        return SubscriptionChangeText.monthlyDowngradeText(nextBillingDate);
      }
    } else if (isSameInterval && currentInterval === ProductInterval.Yearly) {
      if (isUpgrade) {
        return SubscriptionChangeText.yearlyUpgradeText(proratedAmountNode);
      } else if (isDowngrade) {
        return SubscriptionChangeText.yearlyDowngradeText(nextBillingDate);
      }
    }

    return AccountActionText.upgradeText;
  }, [currentProduct, selectedProductData, subscriptionDetails, isUpgrade, isDowngrade, proratedAmountNode]);

  const keepActualSubscription = useCallback(async () => {
    await dispatch(fetchDetails());
    await dispatch(fetchProfile());
  }, [dispatch]);

  const handleUpgrade = useCallback(async () => {
    if (!selectedProduct) {
      return showAlert({
        theme: AlertTheme.ERROR,
        message: "Please select a plan",
      } as AlertOptions);
    }

    let result;
    if (isUpgrade) {
      result = await dispatch(upgradeSubscription({ toPriceId: selectedProduct }));
    } else if (isDowngrade) {
      result = await dispatch(downgradeSubscription({ toPriceId: selectedProduct }));
    } else {
      // same plan selected
      return showAlert({
        theme: AlertTheme.ERROR,
        message: "You are already on this plan",
      } as AlertOptions);
    }

    if (upgradeSubscription.rejected.match(result) || downgradeSubscription.rejected.match(result)) {
      const error = result.payload as { details?: string } | undefined;
      return showAlert({
        theme: AlertTheme.ERROR,
        message: error?.details || "Failed to update subscription",
      } as AlertOptions);
    }

    showAlert({
      theme: AlertTheme.SUCCESS,
      message: isUpgrade ? "Subscription successfully upgraded!" : "Subscription successfully downgraded!",
    } as AlertOptions);

    await keepActualSubscription();
    dispatch(invalidateSubscriptionChangePreview());
  }, [dispatch, showAlert, keepActualSubscription, selectedProduct, isUpgrade, isDowngrade]);

  const handleSkip = useCallback(async () => {
    const { success, err } = await skipSubscription();

    if (!success) {
      return showAlert({
        theme: AlertTheme.ERROR,
        message: err?.details,
      } as AlertOptions);
    }

    showAlert({
      theme: AlertTheme.SUCCESS,
      message: "Subscription successfully skipped!",
    } as AlertOptions);

    await keepActualSubscription();
  }, [showAlert, keepActualSubscription]);

  const handleUnskip = useCallback(async () => {
    const { success, err } = await unskipSubscription();

    if (!success) {
      return showAlert({
        theme: AlertTheme.ERROR,
        message: err?.details,
      } as AlertOptions);
    }

    showAlert({
      theme: AlertTheme.SUCCESS,
      message: "Subscription successfully updated!",
    } as AlertOptions);

    await keepActualSubscription();
  }, [showAlert, keepActualSubscription]);

  const handleCancel = useCallback(async () => {
    const { success, err } = await cancelSubscription();

    if (!success) {
      return showAlert({
        theme: AlertTheme.ERROR,
        message: err?.details,
      } as AlertOptions);
    }

    showAlert({
      theme: AlertTheme.SUCCESS,
      message: "Your membership canceled!",
    } as AlertOptions);

    await keepActualSubscription();
  }, [showAlert, keepActualSubscription]);

  const handleUnCancel = useCallback(async () => {
    const { success, err } = await uncancelSubscription();

    if (!success) {
      return showAlert({
        theme: AlertTheme.ERROR,
        message: err?.details,
      } as AlertOptions);
    }

    showAlert({
      theme: AlertTheme.SUCCESS,
      message: "Your membership is active again!",
    } as AlertOptions);

    await keepActualSubscription();
  }, [showAlert, keepActualSubscription]);

  const handleReactivate = useCallback(
    async (stripePriceId: string) => {
      let response;

      if (subscription.status === SubscriptionStatus.MonthlySkipping) {
        response = await unskipSubscription();
      } else {
        response = await createSubscription(stripePriceId);
      }

      const { success, err } = response;

      if (!success) {
        return showAlert({
          theme: AlertTheme.ERROR,
          message: err?.details,
        } as AlertOptions);
      }

      showAlert({
        theme: AlertTheme.SUCCESS,
        message: "Your membership is active again!",
      } as AlertOptions);

      await keepActualSubscription();
    },
    [showAlert, keepActualSubscription, subscription]
  );

  const planActions = [
    {
      name: "Retry Payment",
      element: <PaymentFailed onUpdate={() => {}} />,
      visibleWhen: [SubscriptionStatus.PaymentFailed],
    },
    {
      name: "Uncancel Membership",
      element: <Button onClick={withModalConfirm(handleUnCancel, AccountActionText.uncancelText)}>REACTIVATE</Button>,
      visibleWhen: [SubscriptionStatus.CancelUpcoming],
    },
    {
      name: "Unskip Membership",
      element: <Button onClick={withModalConfirm(handleUnskip, AccountActionText.unskipText)}>REACTIVATE</Button>,
      visibleWhen: [SubscriptionStatus.MonthlySkipUpcoming],
    },
    {
      name: "Upgrade Plan",
      element: (
        <div className={cls.Block}>
          <Title size={TitleSize.H2}>Change My Plan</Title>
          <ProductSelection
            className={cls.ProductSelection}
            variant={theme === "darkTheme" ? "dark" : "light"}
            stripePriceId={selectedProduct}
            onChange={setSelectedProduct}
          />
          <div>
            <ActionBtn onClick={() => setIsChangePlanModalOpen(true)} disabled={!isDifferentPlanSelected}>
              CHANGE PLAN
            </ActionBtn>
          </div>
          <Dialog
            isOpen={isChangePlanModalOpen}
            isBusy={isChangePlanBusy}
            onClose={() => setIsChangePlanModalOpen(false)}
            title={getChangePlanModalText}
            submit={{
              text: "Yes",
              onClick: async () => {
                setIsChangePlanBusy(true);
                try {
                  await handleUpgrade();
                  setIsChangePlanModalOpen(false);
                } finally {
                  setIsChangePlanBusy(false);
                }
              },
            }}
            cancel={{ text: "No" }}
          />
        </div>
      ),
      visibleWhen: [SubscriptionStatus.MonthlyActive, SubscriptionStatus.YearlyActive],
    },
    {
      name: "Skip Plan",
      element: (
        <div className={cls.Block}>
          <Title size={TitleSize.H2}>Skip My Plan</Title>
          <p>Don't need our services for the next billing cycle? Pause your plan.</p>
          <Button onClick={withModalConfirm(handleSkip, AccountActionText.skipText)}>SKIP</Button>
        </div>
      ),
      visibleWhen: [SubscriptionStatus.MonthlyActive],
    },
    {
      name: "Cancel Membership",
      element: (
        <div className={cls.Block}>
          <Title size={TitleSize.H2}>Cancel Membership</Title>
          <p>
            You can cancel your membership here at any time. We're sad to see you go! Your account will remain on file,
            and you can log in and rejoin Sibyl at any time.
          </p>
          <Button onClick={withModalConfirm(handleCancel, AccountActionText.cancelText)}>CANCEL</Button>
        </div>
      ),
      visibleWhen: [
        SubscriptionStatus.YearlyActive,
        SubscriptionStatus.MonthlyActive,
        SubscriptionStatus.MonthlySkipUpcoming,
        SubscriptionStatus.MonthlySkipping,
      ],
    },
    {
      name: "Reactivate Membership",
      element: (
        <div className={cls.Block}>
          <Title size={TitleSize.H2}>Reactivate your plan</Title>
          <ProductSelection
            className={cls.ProductSelection}
            variant={theme === "darkTheme" ? "dark" : "light"}
            stripePriceId={reactivateStripePriceId}
            onChange={setReactivateStripePriceId}
          />
          <div>
            <ActionBtn
              disabled={!reactivateStripePriceId}
              onClick={() =>
                withModalConfirm(handleReactivate, AccountActionText.reactivateText)(reactivateStripePriceId)
              }
            >
              REACTIVATE
            </ActionBtn>
          </div>
        </div>
      ),
      visibleWhen: [SubscriptionStatus.Canceled, SubscriptionStatus.MonthlySkipping],
    },
  ];

  if (subscription.isFetching) {
    return null;
  }

  const visibleActions = planActions.filter((action) => action.visibleWhen.includes(subscription.status));

  return (
    <div className={clsx(cls.ManagePlan, {}, [className])}>
      {visibleActions.map((action) => (
        <Fragment key={action.name}>{action.element}</Fragment>
      ))}
      {modalNode}
    </div>
  );
});
